import { useAppDisplayModeContext } from '@archipro-design/aria';
import PageHeader from '@modules/project/component/page-header/PageHeader';
import DetailsMobile from '@modules/project/component/details/Details.mobile';
import ContactsMobile from '@modules/project/component/contacts/Contacts.mobile';
import Awards from '@modules/project/component/awards/Awards';
import DetailBannerSection from '~/modules/page-builder/component/detail/detail-banner-section/DetailBannerSection';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import {
    BANNER_WIDTH_FULL_MOBILE,
    FULL_COLUMN_WIDTH_FULL_SCREEN,
} from '~/modules/page-builder/config/column-constants';
import DownloadAction from '~/modules/page-builder/component/detail/detail-banner-section/actions/DownloadAction';
import { getBannerDimensions } from '~/utils/banner-utils';
import { useProjectPreviewRouteData } from '@modules/project/hook/use-project-preview-route-data';
import ProjectBanner from './ProjectBanner';
import { ProjectShowcaseCard } from '../details-accordion/ProjectShowcaseCard';
import SummaryOld from '../summary/SummaryOld';
import { truncateString } from './utils';
import { useEnquiryVisibility } from '~/modules/enquiry/hooks/use-enquiry-visibility';
interface ProjectPageHeaderProps {
    canDownloadBannerImage: boolean;
}

const ProjectPageHeader = ({
    canDownloadBannerImage,
}: ProjectPageHeaderProps) => {
    const project = useProjectPreviewRouteData();
    const { desktop } = useAppDisplayModeContext();
    const { showEnquiryCTA, showSpecifierLoginPrompt } = useEnquiryVisibility(
        project.Professional,
    );
    const bannerSize = getImagesSizes({
        desktop: FULL_COLUMN_WIDTH_FULL_SCREEN,
        mobile: BANNER_WIDTH_FULL_MOBILE,
    });
    const bannerDimensions = getBannerDimensions('project');

    return (
        <>
            {desktop ? (
                <>
                    {project.Banner && (
                        <ProjectBanner
                            project={project}
                            canDownloadBannerImage={canDownloadBannerImage}
                        />
                    )}
                    <div className="mx-12">
                        <div className="mt-12 font-display text-6.5 leading-[130%] font-normal text-[#141414]">
                            About
                        </div>
                        <div className="relative border-t border-[#141414]" />
                        <h1 className="my-12 text-36.5 leading-[100%] font-medium tracking-[-0.045em] text-[#141414]">
                            {truncateString(
                                project.ProjectHeroTitle || project.Title,
                                42,
                            )}
                            .
                        </h1>
                    </div>
                    {project.Subheading && (
                        <h2 className="w-359 pl-12 font-display text-9 leading-[1.1] font-normal tracking-[-0.36px] text-[#161616]">
                            {project.Subheading}
                        </h2>
                    )}
                    <ProjectShowcaseCard details={project} />
                </>
            ) : (
                <>
                    {project.Banner && (
                        <DetailBannerSection
                            {...project.Banner}
                            title={project.Title}
                            dimensions={bannerDimensions}
                            size={bannerSize}
                            isVideoBanner={false}
                            videoBannerUrl={project.Banner.videoBannerUrl ?? ''}
                            showBackButton={false}
                            awards={project.Awards}
                            showOverlay={desktop}
                            actions={[
                                <DownloadAction
                                    key="downloadAction"
                                    canDownload={canDownloadBannerImage}
                                    downloadLink={
                                        project.Banner.downloadLink ?? ''
                                    }
                                    libraryImageID={
                                        project.Banner.libraryImage?.ID ?? 0
                                    }
                                />,
                            ]}
                        />
                    )}
                    <PageHeader data={project} />
                    <div className="md:hidden">
                        {project.Content && <SummaryOld data={project} />}
                    </div>
                    <DetailsMobile data={project} />
                    <ContactsMobile
                        project={project}
                        showEnquiryCTA={showEnquiryCTA}
                        showSpecifierLoginPrompt={showSpecifierLoginPrompt}
                    />
                    {project.Awards.length !== 0 && (
                        <Awards awards={project.Awards} />
                    )}
                </>
            )}
        </>
    );
};

export default ProjectPageHeader;
